import { useRef, useState } from "react";
import { Button } from "@sfc/ui";
import {
  definitionFor,
  FILE_TYPE_PRESETS,
  fileTypePresetOf,
  orderedQuestions,
  orderedSections,
  optionsOf,
  offersOther,
  questionsInFormOrder,
  walkSections,
  type AnswerMap,
  supportsValidator,
  takesAnswer,
  VALIDATION_RULE_LABELS,
  type FormContent,
  type ValidationRule,
} from "@sfc/forms";
import { fileUploadSummary } from "@sfc/forms/react";
import { FormProse } from "./FormProse";

function describeRule(rule: ValidationRule): string {
  if (rule.kind === "regex") {
    return `${rule.negate ? "Must not match pattern" : "Must match pattern"}: ${rule.pattern}`;
  }
  if (rule.kind === "contains") {
    return `${rule.negate ? "Must not contain" : "Must contain"}: “${rule.value}”`;
  }
  return "value" in rule
    ? `${VALIDATION_RULE_LABELS[rule.kind]}: ${rule.value}`
    : VALIDATION_RULE_LABELS[rule.kind];
}

/** Explore section routes with temporary choices, never saved applicant answers. */
export function FormGuideView({
  content,
  applicant = false,
}: {
  content: FormContent;
  applicant?: boolean;
}) {
  const [selection, setSelection] = useState<Record<
    string,
    string | null
  > | null>(null);
  const sectionDrivers = new Set(
    content.sections.flatMap((section) => [
      ...(section.visibleWhen ? [section.visibleWhen.questionId] : []),
      ...(section.jumps ?? []).map((jump) => jump.when.questionId),
    ]),
  );
  // A question controlling whether another route choice is asked also steers
  // the section path. Include these dependencies, but not ordinary follow-ups.
  for (const id of sectionDrivers) {
    const question = content.questions.find((candidate) => candidate.id === id);
    if (question?.visibleWhen)
      sectionDrivers.add(question.visibleWhen.questionId);
  }
  const drivers = questionsInFormOrder(content).filter((question) =>
    sectionDrivers.has(question.id),
  );
  const choices = drivers.filter(
    (question) =>
      (question.type === "multiple_choice" || question.type === "dropdown") &&
      optionsOf(question).length > 0 &&
      !offersOther(question),
  );
  // Keep the full document when a route needs an input this compact panel
  // cannot represent (for example a numeric threshold or an uploaded file).
  const canExplore =
    choices.length > 0 && choices.length === sectionDrivers.size;
  const answers: AnswerMap = {};
  for (const question of choices) {
    if (!question.required && selection?.[question.id] === null) continue;
    const options = optionsOf(question);
    const option =
      options.find((option) => option.id === selection?.[question.id]) ??
      options[0]!;
    answers[question.id] = {
      kind: "choice",
      choice: { kind: "option", optionId: option.id },
    };
  }
  const exploring = canExplore && selection !== null;
  const sections = exploring
    ? walkSections(content, answers)
    : orderedSections(content);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [overview, setOverview] = useState(false);
  const showingOverview = applicant && overview && !!content.description;
  const activeSection =
    sections.find((section) => section.id === activeSectionId) ?? sections[0];
  const sectionNodes = useRef(new Map<string, HTMLElement>());
  const hasBranching =
    content.sections.some(
      (section) => section.visibleWhen || section.jumps?.length,
    ) || content.questions.some((question) => question.visibleWhen);

  return (
    <div className="flex min-w-0 flex-col gap-6 break-words">
      {(!applicant || hasBranching) && (
        <header className="flex flex-col gap-2">
          {!applicant && <h2 className="heading-lg">All questions</h2>}
          {!applicant && (
            <p className="body-md text-muted-foreground">
              Read through the questions and guidance to see what you’ll need to
              prepare. Nothing here needs filling in.
            </p>
          )}
          {content.description && !applicant ? (
            <details
              open={applicant ? true : undefined}
              className="border-b border-border"
            >
              <summary className="body-md cursor-pointer py-3 font-medium">
                Application introduction
              </summary>
              <FormProse
                value={content.description}
                className="body-md pb-4 text-muted-foreground"
              />
            </details>
          ) : null}
          {(!applicant || hasBranching) && (
            <p className="body-sm text-muted-foreground">
              {exploring
                ? "Showing sections for the choices below."
                : "All sections are shown here."}
              {hasBranching
                ? " The sections and questions you’ll answer may depend on your responses."
                : null}
            </p>
          )}
        </header>
      )}
      {canExplore ? (
        <section
          aria-label="Preview choices"
          className="flex flex-col gap-5 border-y border-border py-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h3 className="heading-sm">Preview your application</h3>
              <p className="body-sm text-muted-foreground">
                These choices change which sections you’ll need. Try a path
                without starting an application.
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={() => setSelection(null)}
              aria-pressed={!exploring}
            >
              Show all sections
            </Button>
          </div>
          {choices.map((question) => (
            <fieldset key={question.id} className="flex min-w-0 flex-col gap-2">
              <legend className="body-md mb-2 font-medium">
                {question.label || "Untitled question"}
              </legend>
              <div className="flex flex-wrap gap-2">
                {!question.required ? (
                  <Button
                    variant={
                      exploring && !answers[question.id] ? "default" : "outline"
                    }
                    aria-pressed={exploring && !answers[question.id]}
                    onClick={() =>
                      setSelection({ ...selection, [question.id]: null })
                    }
                  >
                    Leave unanswered
                  </Button>
                ) : null}
                {optionsOf(question).map((option) => {
                  const answer = answers[question.id];
                  const selected =
                    exploring &&
                    answer?.kind === "choice" &&
                    answer.choice.kind === "option" &&
                    answer.choice.optionId === option.id;
                  return (
                    <Button
                      key={option.id}
                      variant={selected ? "default" : "outline"}
                      aria-pressed={selected}
                      className="h-auto min-h-11 max-w-full whitespace-normal break-words text-left"
                      onClick={() =>
                        setSelection({ ...selection, [question.id]: option.id })
                      }
                    >
                      {option.label || "Untitled option"}
                    </Button>
                  );
                })}
              </div>
            </fieldset>
          ))}
          {exploring && choices.length > 1 ? (
            <p className="body-sm text-muted-foreground">
              Other choices start with their first option. You can change any of
              them above.
            </p>
          ) : null}
        </section>
      ) : null}
      {sections.length ? (
        <div className="grid items-start gap-8 lg:grid-cols-[14rem_minmax(0,1fr)]">
          <nav
            aria-label="Application guide sections"
            className="flex min-w-0 flex-col gap-1 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-10rem)] lg:overflow-y-auto"
          >
            {applicant && content.description && (
              <Button
                variant="nav"
                aria-current={showingOverview ? "step" : undefined}
                onClick={() => setOverview(true)}
                className="h-auto min-h-11 justify-start rounded-none border-l-2 border-transparent px-3 py-3 text-left aria-[current=step]:border-primary aria-[current=step]:bg-card aria-[current=step]:text-primary"
              >
                Overview
              </Button>
            )}
            {sections.map((section, index) => (
              <Button
                key={section.id}
                variant="nav"
                aria-current={
                  applicant &&
                  !showingOverview &&
                  activeSection?.id === section.id
                    ? "step"
                    : undefined
                }
                onClick={() => {
                  if (applicant) {
                    setOverview(false);
                    setActiveSectionId(section.id);
                    return;
                  }
                  const node = sectionNodes.current.get(section.id);
                  node?.scrollIntoView({ block: "start" });
                  node?.focus({ preventScroll: true });
                }}
                className="h-auto min-h-11 justify-start whitespace-normal rounded-none border-l-2 border-transparent px-3 py-3 text-left aria-[current=step]:border-primary aria-[current=step]:bg-card aria-[current=step]:text-primary"
              >
                <span className="shrink-0 text-muted-foreground">
                  {index + 1}.
                </span>
                <span className="min-w-0 break-words">
                  {section.title || "Untitled section"}
                </span>
              </Button>
            ))}
          </nav>
          <div className="flex min-w-0 flex-col gap-12">
            {showingOverview && (
              <section className="space-y-5">
                <h3 className="application-section-title">Overview</h3>
                <FormProse value={content.description!} className="body-md" />
              </section>
            )}

            {(applicant
              ? sections.filter(
                  (section) =>
                    !showingOverview && section.id === activeSection?.id,
                )
              : sections
            ).map((selected) => {
              const questions = orderedQuestions(content, selected.id);
              return (
                <section
                  key={selected.id}
                  ref={(node) => {
                    if (node) sectionNodes.current.set(selected.id, node);
                    else sectionNodes.current.delete(selected.id);
                  }}
                  tabIndex={-1}
                  className="min-w-0 scroll-mt-20"
                  role="region"
                  aria-label={selected.title || "Untitled section"}
                >
                  <header className="flex flex-col gap-2 pb-4">
                    <h3 className="application-section-title break-words">
                      {selected.title || "Untitled section"}
                    </h3>
                    {selected.description ? (
                      <FormProse
                        value={selected.description}
                        className="body-md text-muted-foreground"
                      />
                    ) : null}
                    {selected.visibleWhen ? (
                      <p className="body-sm text-muted-foreground">
                        This section may apply depending on your answers.
                      </p>
                    ) : null}
                  </header>
                  <div className="flex flex-col divide-y divide-border/60 border-t border-border">
                    {questions.length === 0 ? (
                      <p className="body-md py-4 text-muted-foreground">
                        No questions in this section yet.
                      </p>
                    ) : null}
                    {questions.map((question) => {
                      const answerable = takesAnswer(question.type);
                      const description = question.shortDescription?.trim()
                        ? question.shortDescription
                        : undefined;
                      const rules = (question.validation ?? []).filter((rule) =>
                        supportsValidator(question.type, rule.kind),
                      );
                      const filePreset = FILE_TYPE_PRESETS.find(
                        (preset) =>
                          preset.key ===
                          fileTypePresetOf(question.config?.acceptedTypes),
                      );
                      return (
                        <article
                          key={question.id}
                          className={
                            applicant
                              ? "flex min-w-0 flex-col gap-2 py-3"
                              : "flex min-w-0 flex-col gap-2 py-4"
                          }
                        >
                          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                            <h4 className="body-md min-w-0 flex-1 basis-full font-medium break-words sm:basis-48">
                              {question.label ||
                                (answerable
                                  ? "Untitled question"
                                  : "Information")}
                            </h4>
                            {answerable ? (
                              <p className="body-sm text-muted-foreground">
                                {question.required ? "Required" : "Optional"} •{" "}
                                {definitionFor(question.type).label}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex min-w-0 flex-col gap-2 empty:hidden">
                            {question.visibleWhen ? (
                              <p className="body-sm text-muted-foreground">
                                This question may apply depending on your
                                answers.
                              </p>
                            ) : null}
                            {description ? (
                              <p className="body-sm whitespace-pre-wrap break-words text-muted-foreground">
                                {description}
                              </p>
                            ) : null}
                            {rules.length ? (
                              <ul className="body-sm flex list-disc flex-col gap-1 pl-5 text-muted-foreground">
                                {rules.map((rule, index) => (
                                  <li
                                    key={`${rule.kind}-${index}`}
                                    className="break-words"
                                  >
                                    {describeRule(rule)}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {question.type === "file_upload" ? (
                              <p className="body-sm text-muted-foreground">
                                {fileUploadSummary(question)} •{" "}
                                {filePreset?.label ??
                                  question.config?.acceptedTypes?.join(", ")}
                              </p>
                            ) : null}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="body-md text-muted-foreground">
          Add a section to see the application guide.
        </p>
      )}
    </div>
  );
}
